/**
 * Lapsed Subscription Modal
 *
 * Similar to ExpiredTrialModal, this modal is shown when a user's subscription
 * has lapsed, expired, or is past due. It provides options to reactivate or
 * update their subscription.
 */

import { useLogout } from "@/hooks/auth-queries";
import { useUserStore } from "@/stores/user-store";
import {
	CreditCardOutlined,
	ExclamationCircleOutlined,
	LogoutOutlined,
} from "@ant-design/icons";
import { Alert, Button, Modal, Space, Typography } from "antd";
import React from "react";
import { useNavigate } from "react-router-dom";

const { Text, Paragraph } = Typography;

interface LapsedSubscriptionModalProps {
	open: boolean;
	onClose: () => void;
	subscriptionStatus?: "canceled" | "past_due" | "expired" | "none";
}

export const LapsedSubscriptionModal: React.FC<
	LapsedSubscriptionModalProps
> = ({ open, onClose, subscriptionStatus = "expired" }) => {
	const logout = useLogout();
	const navigate = useNavigate();
	const user = useUserStore((state) => state.user);

	// Super admins (access_level >= 1000) should never see this modal
	// Check both snake_case and camelCase versions for compatibility
	const accessLevel = user?.role?.accessLevel ?? user?.role?.access_level ?? 0;
	const isSuperAdmin = accessLevel >= 1000;
	if (isSuperAdmin) {
		return null;
	}

	const handleLogout = async () => {
		try {
			await logout();
			// The logout hook will handle navigation
		} catch (error) {
			console.error("Logout failed:", error);
		}
	};

	const handleManageSubscription = () => {
		// Close the modal and navigate to subscription page
		onClose();
		navigate("/subscription");
	};

	const getModalContent = () => {
		switch (subscriptionStatus) {
			case "canceled":
				return {
					title: "Subscription Canceled",
					alertMessage: "Your subscription has been canceled",
					alertDescription:
						"To continue using FacilityAI, please reactivate your subscription or choose a new plan.",
					bodyText:
						"Your account and data are still safe. Reactivate your subscription to continue accessing all features.",
				};
			case "past_due":
				return {
					title: "Payment Past Due",
					alertMessage: "Your subscription payment is past due",
					alertDescription:
						"Please update your payment method or resolve any billing issues to continue using FacilityAI.",
					bodyText:
						"Your access may be limited until payment is resolved. Update your payment information to restore full access.",
				};
			case "none":
				return {
					title: "No Active Subscription",
					alertMessage: "You don't have an active subscription",
					alertDescription:
						"To access FacilityAI features, please choose a subscription plan.",
					bodyText:
						"Subscribe now to unlock powerful facility management tools and keep your data accessible.",
				};
			default:
				return {
					title: "Subscription Expired",
					alertMessage: "Your subscription has expired",
					alertDescription:
						"To continue using FacilityAI, please renew your subscription.",
					bodyText:
						"Your account and data are still safe. Renew your subscription to continue managing your facilities.",
				};
		}
	};

	const content = getModalContent();

	return (
		<Modal
			title={
				<Space>
					<ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
					<span>{content.title}</span>
				</Space>
			}
			open={open}
			onCancel={onClose}
			footer={[
				<Button key="logout" size="large" onClick={handleLogout}>
					<LogoutOutlined />
					Logout
				</Button>,
				<Button
					key="manage"
					type="primary"
					size="large"
					onClick={handleManageSubscription}
				>
					<CreditCardOutlined />
					{subscriptionStatus === "none"
						? "Subscribe Now"
						: "Manage Subscription"}
				</Button>,
			]}
			width={500}
			centered
			closable={false}
			maskClosable={false}
		>
			<div style={{ textAlign: "center", padding: "20px 0" }}>
				<Alert
					message={content.alertMessage}
					description={content.alertDescription}
					type="warning"
					showIcon
					style={{ marginBottom: "24px" }}
				/>

				<Paragraph>
					<Text strong>Don't lose your progress!</Text>
				</Paragraph>

				<Paragraph>{content.bodyText}</Paragraph>

				<div style={{ marginTop: "24px" }}>
					<Text type="secondary">
						{subscriptionStatus === "past_due"
							? "Update your payment method or contact support if you need assistance."
							: "Choose from our flexible pricing plans designed for organizations of all sizes."}
					</Text>
				</div>
			</div>
		</Modal>
	);
};
